import { render } from "preact";
import { lazy, Suspense } from "preact/compat";
import { useEffect, useState } from "preact/hooks";
import { match, onRouteChange, type Route } from "./router";
import { pageView } from "./lib/pageview";
import { matchLocale, setLocale } from "./i18n";
import { Shell } from "./components/Shell";
import { Toaster } from "./components/Toaster";
import "./styles/tokens.css";
import "./styles/base.css";
import { applyTheme } from "./lib/theme";
import { captureFirstVisit } from "./lib/firstVisit";

applyTheme();
// A device's first visit: where it came from, kept for its first poll (never under GPC or DNT).
captureFirstVisit();

// Each screen is its own chunk, so a voter downloads only the vote screen.
const Vote = lazy(() => import("./screens/Vote"));
const Presenter = lazy(() => import("./screens/Presenter"));
const CreatePage = lazy(() => import("./screens/CreatePage"));
const Join = lazy(() => import("./screens/Join"));
const Remote = lazy(() => import("./screens/Remote"));
const MyPolls = lazy(() => import("./screens/MyPolls"));
const Admin = lazy(() => import("./screens/Admin"));
const SignIn = lazy(() => import("./screens/SignIn"));
const SignInConfirm = lazy(() => import("./screens/SignInConfirm"));
const Upgrade = lazy(() => import("./screens/Upgrade"));
const Help = lazy(() => import("./screens/Help"));
const CheckoutPaddle = lazy(() => import("./screens/Checkout").then((m) => ({ default: m.CheckoutPaddle })));
const CheckoutFake = lazy(() => import("./screens/Checkout").then((m) => ({ default: m.CheckoutFake })));
const CheckoutSuccess = lazy(() => import("./screens/Checkout").then((m) => ({ default: m.CheckoutSuccess })));
const NotFound = lazy(() => import("./screens/NotFound"));

function App() {
  const [route, setRoute] = useState<Route>(() => match(location.pathname, location.search));
  useEffect(() => onRouteChange(() => setRoute(match(location.pathname, location.search))), []);
  // One page view per screen shown, by type, including client-side navigation (lib/pageview).
  useEffect(() => pageView(route.name), [route]);
  let screen;
  switch (route.name) {
    case "vote":
      screen = <Vote key={route.code} code={route.code} />;
      break;
    case "present":
      screen = <Presenter key={route.code} code={route.code} />;
      break;
    case "create":
      screen = <CreatePage from={route.from} template={route.template} fromVote={route.fromVote} category={route.category} />;
      break;
    case "join":
      screen = <Join />;
      break;
    case "remote":
      screen = <Remote key={route.code} code={route.code} />;
      break;
    case "my":
      screen = <MyPolls />;
      break;
    case "admin":
      screen = <Admin />;
      break;
    case "signin":
      screen = <SignIn />;
      break;
    case "signin-confirm":
      screen = <SignInConfirm />;
      break;
    case "upgrade":
      screen = <Upgrade />;
      break;
    case "help":
      screen = <Help />;
      break;
    case "checkout":
      screen = <CheckoutPaddle />;
      break;
    case "checkout-fake":
      screen = <CheckoutFake />;
      break;
    case "checkout-success":
      screen = <CheckoutSuccess />;
      break;
    default:
      screen = <NotFound />;
  }
  const body = <Suspense fallback={<main class="message loading" />}>{screen}</Suspense>;
  // The vote page and the big screen are full focus; everything else wears the same frame.
  if (route.name === "vote" || route.name === "present") return body;
  return <Shell here={route.name}>{body}</Shell>;
}

// The offline shell and installability (BACKLOG #13 PWA). Never caches the API.
if ("serviceWorker" in navigator && isSecureContext) {
  addEventListener("load", () => void navigator.serviceWorker.register("/sw.js").catch(() => {}));
}

// The phone's language decides (BACKLOG #22); a choice made in the app wins.
const chosen = (() => {
  try {
    return new URLSearchParams(location.search).get("lang") ?? localStorage.getItem("flp_lang");
  } catch {
    return null;
  }
})();
const loc = matchLocale(chosen ? [chosen] : (navigator.languages ?? [navigator.language]));
(loc === "en" ? Promise.resolve() : setLocale(loc)).finally(() => render(
    <>
      <App />
      <Toaster />
    </>,
    document.getElementById("app")!,
  ));


// Keyboard focus rings only after Tab; any click or tap hides them again.
addEventListener("keydown", (e) => { if (e.key === "Tab") document.documentElement.classList.add("kbd"); }, true);
addEventListener("pointerdown", () => document.documentElement.classList.remove("kbd"), true);
